import "./Profile.css";
import React, { useState, useContext, createContext, Fragment } from "react";
import { NavLink, Routes, Route } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    Home,
    Box,
    Camera,
    ShoppingCart,
    Package,
    BriefcaseBusiness,
    Users,
    Menu,
    X,
    ClipboardList,
    Download,
} from "lucide-react";

import ListasDirecciones from "./components/ListaDirecciones/ListasDirecciones.jsx";
import Editor from "./components/Editor/Editor.jsx";
import ListaModelosLlaveros from "./pages/Modelos/ListaModelosLlaveros.jsx";
import ListaMedios from "./pages/Medios/ListaMedios.jsx";
import { MisProyectosCliente, MiProyectoCliente } from "./pages/ProyectosCliente/ProyectosCliente.jsx";
import UserCustomOrderRequests from "./pages/CustomOrderRequests/UserCustomOrderRequests.jsx";
import UserOrders from "./pages/Orders/UserOrders.jsx";
import UserOrderDetail from "./pages/Orders/UserOrderDetail.jsx";
import ClientDashboard from "./pages/Dashboard/ClientDashboard.jsx";
import MisDescargas from "./pages/Descargas/MisDescargas.jsx";

import useMediaQuery from "../../components/tools/useMediaQuery.jsx";
import { useEditor } from "../../App.jsx";
import { useAuthContext } from "../../components/providers/AuthProvider.jsx";
import UserProfile from "./components/UserProfile.jsx";
import UltimosModelosCreados from "./components/UltimosModelosCreados/UltimosModelosCreados.jsx";

// Contexts
const ClienteContext = createContext();
const ProyectoContext = createContext();
const PresupuestoContext = createContext();

export const useCliente = () => useContext(ClienteContext);
export const useProyecto = () => useContext(ProyectoContext);
export const usePresupuesto = () => useContext(PresupuestoContext);

const ClienteProvider = ({ children }) => {
    const [cliente, setCliente] = useState(null);
    return (
        <ClienteContext.Provider value={{ cliente, setCliente }}>
            {children}
        </ClienteContext.Provider>
    );
};

const ProyectoProvider = ({ children }) => {
    const [proyecto, setProyecto] = useState(null);
    return (
        <ProyectoContext.Provider value={{ proyecto, setProyecto }}>
            {children}
        </ProyectoContext.Provider>
    );
};

const PresupuestoProvider = ({ children }) => {
    const [presupuesto, setPresupuesto] = useState(null);
    return (
        <PresupuestoContext.Provider value={{ presupuesto, setPresupuesto }}>
            {children}
        </PresupuestoContext.Provider>
    );
};

// Wrapper para editor de proyectos
export const ProyectoEditorWrapper = ({ id, setEditor, setEditorID, editorType, setEditorType }) => (
    <PresupuestoProvider>
        <ProyectoProvider>
            <ClienteProvider>
                <Editor
                    id={id}
                    setEditor={setEditor}
                    setEditorID={setEditorID}
                    editorType={editorType}
                    setEditorType={setEditorType}
                />
            </ClienteProvider>
        </ProyectoProvider>
    </PresupuestoProvider>
);

// Componente principal
export default function Profile2() {
    const authContext = useAuthContext();
    const { stateEditor } = useEditor();
    const isMobile = useMediaQuery("(max-width: 1024px)");
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const navItems = [
        { to: "general", icon: Home, label: "General" },
        { to: "modelos_creados", icon: Box, label: "Modelos creados" },
        { to: "medios", icon: Camera, label: "Medios" },
        { to: "mis-pedidos", icon: Package, label: "Mis pedidos" },
        { to: "mis-descargas", icon: Download, label: "Mis descargas" },
        { to: "mis-solicitudes", icon: ClipboardList, label: "Mis solicitudes" },
        { to: "proyectos", icon: BriefcaseBusiness, label: "Mis proyectos" },
        /*...(authContext.role === "admin"
            ? [
                { to: "clientes", icon: Users, label: "Clientes (Admin)" },
                { to: "productos", icon: Package, label: "Productos (Admin)" },
                { to: "todo_modelos_creados", icon: Box, label: "Modelos Todos (Admin)" },
                { to: "enlaces_publicos", icon: Box, label: "Enlaces públicos (Admin)" },
            ]
            : [])*/
    ];

    const activeClass = "bg-neutral text-base-300 font-bold p-3 rounded-lg transition-colors duration-200";
    const inactiveClass = "text-neutral hover:bg-secondary hover:text-white p-3 rounded-lg transition-colors duration-200";

    return (
        <div className="w-full h-full min-h-full max-h-full p-3 flex flex-col lg:flex-row gap-3">
            {/* Móvil: botón + menú */}
            {!stateEditor?.editor && isMobile && (
                <div className="w-full">
                    <button
                        onClick={() => setMobileMenuOpen((prev) => !prev)}
                        className="w-full flex justify-between items-center glassCard text-white p-3 rounded-lg mb-2"
                    >
                        <span>Menú</span>
                        {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                    <AnimatePresence initial={false}>
                        {mobileMenuOpen && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                className="overflow-hidden rounded-lg glassCard"
                            >
                                <nav className="flex flex-col space-y-2 p-2 max-h-[60vh] overflow-y-auto">
                                    {navItems.map(({ to, icon: Icon, label }) => (
                                        <NavLink
                                            key={to}
                                            to={to}
                                            className={({ isActive }) => (isActive ? activeClass : inactiveClass)}
                                            onClick={() => setMobileMenuOpen(false)}
                                        >
                                            <Icon className="w-5 h-5 mr-3 inline" />
                                            <span className="text-sm">{label}</span>
                                        </NavLink>
                                    ))}
                                </nav>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            )}

            {/* Escritorio: menú fijo */}
            {!stateEditor?.editor && !isMobile && (
                <aside className="w-2/12 glassCard rounded-lg p-1">
                    <nav className="flex flex-col space-y-2 overflow-y-auto max-h-full p-1">
                        {navItems.map(({ to, icon: Icon, label }) => (
                            <NavLink
                                key={to}
                                to={to}
                                className={({ isActive }) => (isActive ? activeClass : inactiveClass)}
                            >
                                <Icon className="w-5 h-5 mr-3 inline" />
                                <span className="text-sm">{label}</span>
                            </NavLink>
                        ))}
                    </nav>
                </aside>
            )}

            {/* Contenido principal */}
            <main className="w-full lg:w-10/12 flex-1 overflow-y-auto overflow-x-hidden">
                {stateEditor?.editor ? (
                    <ProyectoEditorWrapper
                        setEditor={() => {}}
                        editorType={null}
                        id={null}
                        setEditorID={() => {}}
                        setEditorType={() => {}}
                    />
                ) : (
                    <Routes>
                        <Route
                            path="general"
                            element={<ClientDashboard />}
                            /*element={
                                <div className="max-h-full h-full  space-y-4 z-10">
                                    <UserProfile />
                                    <UltimosModelosCreados />
                                </div>
                            }*/
                        />

                        <Route path="modelos_creados" element={<ListaModelosLlaveros />} />
                        <Route path="medios" element={<ListaMedios />} />
                        <Route path="mis-pedidos" element={<UserOrders />} />
                        <Route path="mis-pedidos/:orderNumber" element={<UserOrderDetail />} />
                        <Route path="mis-descargas" element={<MisDescargas />} />
                        <Route path="mis-solicitudes" element={<UserCustomOrderRequests />} />
                        {/* Compatibilidad con la ruta antigua "pedidos" → redirige a solicitudes */}
                        <Route path="pedidos" element={<UserCustomOrderRequests />} />
                        {/* C3D-38: los proyectos que el equipo le prepara al usuario. El
                            «Proyectos» antiguo (gestionproyectos) ya no se enruta. */}
                        <Route path="proyectos" element={<MisProyectosCliente />} />
                        <Route path="proyectos/:projectId" element={<MiProyectoCliente />} />
                        <Route path="*" element={<div>Seleccione una opción del menú</div>} />
                    </Routes>
                )}
            </main>
        </div>
    );
}
